import { DragState, GameState, GRID, Layout, Piece, Theme } from './types';
import { canPlace } from './game';
import { t } from './i18n';

// Height of a tray slot (background card), keyed off trayCell — single source of
// truth shared with input.ts hit-testing so the hit zone matches what's drawn.
export function traySlotH(trayCell: number): number {
  return 5 * trayCell + 12;
}

export function computeLayout(W: number, H: number): Layout {
  // totalH = scoreH(56) + gap(8) + 8*cell + gap(20) + 5*trayCell(=cell*0.62) + pad(24) + bottom(8)
  //        = 116 + cell * (8 + 5 * 0.62) = 116 + cell * 11.1
  const TRAY_RATIO = 0.62;
  const FIXED_H    = 56 + 8 + 20 + 24 + 8; // 116
  const cellFromH  = Math.floor((H - FIXED_H) / (GRID + 5 * TRAY_RATIO));
  const cellFromW  = Math.floor((W - 24) / GRID);
  const cell       = Math.min(cellFromH, cellFromW, 80); // cap 80px on large screens
  const gridW      = cell * GRID;
  const trayCell   = Math.max(Math.floor(cell * TRAY_RATIO), 18);
  const trayH      = 5 * trayCell + 24;
  const scoreH     = 56;
  const totalH     = scoreH + 8 + gridW + 20 + trayH + 8;
  // Ensure enough room for score + best text above the grid (best text reaches ~cell*1.16 above gridY)
  const minStartY  = Math.max(16, Math.ceil(cell * 1.2) - scoreH + 12);
  const startY     = Math.max(minStartY, (H - totalH) / 2);
  const gridX      = Math.round((W - gridW) / 2);
  const gridY      = Math.round(startY + scoreH + 8);
  const trayY      = gridY + gridW + 20;
  const slotW      = W / 3;
  const traySlotX: [number, number, number] = [
    Math.round(slotW * 0.5),
    Math.round(slotW * 1.5),
    Math.round(slotW * 2.5),
  ];
  return { cell, gridX, gridY, trayY, trayCell, traySlotX, W, H };
}

// ── Helpers ──────────────────────────────────────────────────────────────────

function roundRect(
  ctx: CanvasRenderingContext2D,
  x: number, y: number, w: number, h: number, r: number,
): void {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + w - r, y);
  ctx.arcTo(x + w, y, x + w, y + r, r);
  ctx.lineTo(x + w, y + h - r);
  ctx.arcTo(x + w, y + h, x + w - r, y + h, r);
  ctx.lineTo(x + r, y + h);
  ctx.arcTo(x, y + h, x, y + h - r, r);
  ctx.lineTo(x, y + r);
  ctx.arcTo(x, y, x + r, y, r);
  ctx.closePath();
}

function drawBlock(
  ctx: CanvasRenderingContext2D,
  x: number, y: number, size: number,
  color: string, alpha = 1,
): void {
  const r = Math.max(4, size * 0.14);
  const p = 2;
  const w = size - p * 2;
  const h = size - p * 2;

  ctx.save();
  ctx.globalAlpha = alpha;

  // Clip to rounded shape
  roundRect(ctx, x + p, y + p, w, h, r);
  ctx.clip();

  // Main body
  ctx.fillStyle = color;
  ctx.fillRect(x + p, y + p, w, h);

  // Top highlight
  ctx.fillStyle = 'rgba(255,255,255,0.32)';
  ctx.fillRect(x + p, y + p, w, h * 0.42);

  // Bottom shadow
  ctx.fillStyle = 'rgba(0,0,0,0.26)';
  ctx.fillRect(x + p, y + p + h * 0.72, w, h * 0.28);

  ctx.restore();
}

export function readTheme(): Theme {
  const s = getComputedStyle(document.documentElement);
  const v = (name: string) => s.getPropertyValue(name).trim();
  return {
    bg:          v('--bg'),
    gridBg:      v('--grid-bg'),
    gridBorder:  v('--grid-border'),
    emptyCell:   v('--empty-cell'),
    cellBorder:  v('--cell-border'),
    colors: {
      red:    v('--col-red'),
      orange: v('--col-orange'),
      yellow: v('--col-yellow'),
      green:  v('--col-green'),
      blue:   v('--col-blue'),
      purple: v('--col-purple'),
      cyan:   v('--col-cyan'),
    },
    textUi:      v('--text-ui'),
    textDim:     v('--text-dim'),
    overlayBg:   v('--overlay-bg'),
    overlayText: v('--overlay-text'),
    overlaySub:  v('--overlay-sub'),
  };
}

// ── Ghost computation ─────────────────────────────────────────────────────────

// Top-left pixel of the dragged piece, given the finger/pointer position.
// Piece is rendered above the finger, centered on it.
export function dragTopLeft(
  drag: DragState,
  piece: Piece,
  layout: Layout,
): { x: number; y: number } {
  const { cell } = layout;
  const x = drag.x - piece.cols * cell / 2;
  const y = drag.y - cell * 1.5 - piece.rows * cell / 2;
  return { x, y };
}

export function getGhostCell(
  drag: DragState,
  piece: Piece,
  layout: Layout,
): { row: number; col: number } | null {
  const { cell, gridX, gridY } = layout;
  const { x: topLeftX, y: topLeftY } = dragTopLeft(drag, piece, layout);
  const col = Math.round((topLeftX - gridX) / cell);
  const row = Math.round((topLeftY - gridY) / cell);
  return { row, col };
}

// ── Tray piece rendering ──────────────────────────────────────────────────────

function drawPieceInTray(
  ctx: CanvasRenderingContext2D,
  piece: Piece,
  cx: number, cy: number,
  trayCell: number,
  theme: Theme,
  alpha = 1,
): void {
  const tc = trayCell;
  const ox = cx - piece.cols * tc / 2;
  const oy = cy - piece.rows * tc / 2;
  for (const { r, c } of piece.cells) {
    drawBlock(ctx, ox + c * tc, oy + r * tc, tc, theme.colors[piece.color], alpha);
  }
}

// ── Main render ───────────────────────────────────────────────────────────────

export function renderFrame(
  ctx: CanvasRenderingContext2D,
  W: number, H: number,
  layout: Layout,
  state: GameState,
  theme: Theme,
  nowMs: number,
  drag: DragState | null,
  pendingStart: boolean,
): void {
  const { cell, gridX, gridY, trayY, trayCell, traySlotX } = layout;

  ctx.globalAlpha = 1;
  ctx.shadowBlur  = 0;

  // ── Background ──────────────────────────────────────────────────────────────
  ctx.fillStyle = theme.bg;
  ctx.fillRect(0, 0, W, H);

  // ── Grid background + border ─────────────────────────────────────────────────
  const pad = 4;
  roundRect(ctx, gridX - pad, gridY - pad, cell * GRID + pad * 2, cell * GRID + pad * 2, 12);
  ctx.fillStyle = theme.gridBg;
  ctx.fill();
  ctx.strokeStyle = theme.gridBorder;
  ctx.lineWidth = 2;
  ctx.stroke();

  // ── Empty cells ──────────────────────────────────────────────────────────────
  for (let r = 0; r < GRID; r++) {
    for (let c = 0; c < GRID; c++) {
      const x = gridX + c * cell;
      const y = gridY + r * cell;
      const p = 2;
      ctx.save();
      roundRect(ctx, x + p, y + p, cell - p * 2, cell - p * 2, 4);
      ctx.clip();
      // Base fill
      ctx.fillStyle = theme.emptyCell;
      ctx.fillRect(x + p, y + p, cell - p * 2, cell - p * 2);
      // Top highlight (inset look)
      ctx.fillStyle = 'rgba(255,255,255,0.07)';
      ctx.fillRect(x + p, y + p, cell - p * 2, 3);
      // Bottom shadow
      ctx.fillStyle = 'rgba(0,0,0,0.18)';
      ctx.fillRect(x + p, y + cell - p - 3, cell - p * 2, 3);
      ctx.restore();
      // Cell border
      roundRect(ctx, x + p, y + p, cell - p * 2, cell - p * 2, 4);
      ctx.strokeStyle = theme.cellBorder;
      ctx.lineWidth = 0.8;
      ctx.stroke();
    }
  }

  // ── Placed blocks ────────────────────────────────────────────────────────────
  const { clearAnim } = state;
  const isClearing = clearAnim !== null;
  const clearProgress = isClearing
    ? Math.min(1, (nowMs - clearAnim.startMs) / clearAnim.durationMs)
    : 0;

  for (let r = 0; r < GRID; r++) {
    for (let c = 0; c < GRID; c++) {
      const color = state.grid[r][c];
      if (!color) continue;

      const inClearLine = isClearing && (
        clearAnim.rows.includes(r) || clearAnim.cols.includes(c)
      );

      let alpha = 1;
      if (inClearLine) {
        // Flash then fade: 0→0.5 flash white, 0.5→1 fade out
        alpha = clearProgress < 0.5
          ? 1
          : 1 - (clearProgress - 0.5) * 2;
      }

      const x = gridX + c * cell;
      const y = gridY + r * cell;

      if (inClearLine && clearProgress < 0.5) {
        // White flash
        drawBlock(ctx, x, y, cell, '#ffffff', alpha);
      } else {
        drawBlock(ctx, x, y, cell, theme.colors[color], alpha);
      }
    }
  }

  // ── Ghost preview ─────────────────────────────────────────────────────────────
  if (drag && state.phase === 'placing') {
    const piece = state.tray[drag.trayIdx];
    if (piece) {
      const ghost = getGhostCell(drag, piece, layout);
      if (ghost) {
        const valid = canPlace(state.grid, piece, ghost.row, ghost.col);
        const ghostAlpha = valid ? 0.55 : 0.2;
        const ghostColor = valid ? theme.colors[piece.color] : '#888888';
        for (const { r, c } of piece.cells) {
          const x = gridX + (ghost.col + c) * cell;
          const y = gridY + (ghost.row + r) * cell;
          // Only render if within grid
          if (ghost.col + c >= 0 && ghost.col + c < GRID &&
              ghost.row + r >= 0 && ghost.row + r < GRID) {
            drawBlock(ctx, x, y, cell, ghostColor, ghostAlpha);
          }
        }
      }
    }
  }

  // ── Tray ──────────────────────────────────────────────────────────────────────
  for (let i = 0; i < 3; i++) {
    const piece = state.tray[i];
    const cx = traySlotX[i];
    const slotW = W / 3;
    const cy = trayY + (5 * trayCell) / 2 + 8;

    // Slot background
    const slotX = cx - slotW * 0.42;
    const slotH = traySlotH(trayCell);
    roundRect(ctx, slotX, trayY, slotW * 0.84, slotH, 10);
    ctx.fillStyle = 'rgba(255,255,255,0.05)';
    ctx.fill();

    if (piece && !(drag && drag.trayIdx === i)) {
      drawPieceInTray(ctx, piece, cx, cy, trayCell, theme);
    }
  }

  // ── Particles ─────────────────────────────────────────────────────────────────
  for (const p of state.particles) {
    ctx.globalAlpha = p.life;
    ctx.fillStyle   = p.color;
    ctx.beginPath();
    ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.globalAlpha = 1;

  // ── Score ─────────────────────────────────────────────────────────────────────
  const scoreY = layout.gridY - 8;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'bottom';

  // Score
  ctx.fillStyle = theme.textUi;
  ctx.font = `bold ${Math.round(cell * 0.75)}px -apple-system, sans-serif`;
  ctx.fillText(String(state.score), W / 2, scoreY);

  // Best
  if (state.best > 0) {
    ctx.fillStyle = theme.textDim;
    ctx.font = `${Math.round(cell * 0.38)}px -apple-system, sans-serif`;
    ctx.fillText(t('best', { n: state.best }), W / 2, scoreY - Math.round(cell * 0.78));
  }

  // Combo
  if (state.combo >= 2) {
    ctx.fillStyle = '#ffd32a';
    ctx.font = `bold ${Math.round(cell * 0.5)}px -apple-system, sans-serif`;
    ctx.fillText(t('combo', { n: state.combo }), W / 2, scoreY - Math.round(cell * 0.4));
  }

  ctx.textBaseline = 'alphabetic';

  // ── Game Over overlay ─────────────────────────────────────────────────────────
  if (state.phase === 'game_over') {
    ctx.fillStyle = theme.overlayBg;
    ctx.fillRect(0, 0, W, H);

    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';

    ctx.fillStyle = theme.overlayText;
    ctx.font = `bold ${Math.round(cell * 1.1)}px -apple-system, sans-serif`;
    ctx.fillText(t('game_over'), W / 2, H / 2 - cell * 1.2);

    ctx.font = `${Math.round(cell * 0.7)}px -apple-system, sans-serif`;
    ctx.fillText(t('score', { n: state.score }), W / 2, H / 2 - cell * 0.2);

    if (state.score >= state.best && state.score > 0) {
      ctx.fillStyle = '#ffd32a';
      ctx.font = `bold ${Math.round(cell * 0.5)}px -apple-system, sans-serif`;
      ctx.fillText(t('new_record'), W / 2, H / 2 + cell * 0.6);
    }

    ctx.fillStyle = theme.overlaySub;
    ctx.font = `${Math.round(cell * 0.45)}px -apple-system, sans-serif`;
    ctx.fillText(t('tap_to_play'), W / 2, H / 2 + cell * 1.4);

    ctx.textBaseline = 'alphabetic';
  }

  // ── Pending start overlay ─────────────────────────────────────────────────────
  if (pendingStart) {
    ctx.fillStyle = 'rgba(0,0,0,0.55)';
    ctx.fillRect(0, 0, W, H);
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillStyle = '#ffffff';
    ctx.font = `bold ${Math.round(cell * 0.85)}px -apple-system, sans-serif`;
    ctx.fillText(t('tap_to_start'), W / 2, H / 2);
    ctx.textBaseline = 'alphabetic';
  }

  // ── Dragged piece (on top of everything) ──────────────────────────────────────
  if (drag && state.phase === 'placing') {
    const piece = state.tray[drag.trayIdx];
    if (piece) {
      const tc = cell;
      const { x: ox, y: oy } = dragTopLeft(drag, piece, layout);
      for (const { r, c } of piece.cells) {
        drawBlock(ctx, ox + c * tc, oy + r * tc, tc, theme.colors[piece.color], 0.9);
      }
    }
  }
}

// ── Menu (☰ → overlay: Resume / Restart / Home) ────────────────────────────────
// menuRects() is the single source of geometry — shared by drawMenu() below and
// the tap hit-test in main.ts, so draw and hit-test can never diverge.
export interface Rect { x: number; y: number; w: number; h: number; }
export interface Circle { cx: number; cy: number; r: number; }

const MENU_TOGGLE = 40; // диаметр ☰-кнопки
const MENU_EDGE   = 14; // отступ от края

export function menuRects(viewW: number, viewH: number) {
  const toggle: Circle = { cx: MENU_EDGE + MENU_TOGGLE / 2, cy: MENU_EDGE + MENU_TOGGLE / 2, r: MENU_TOGGLE / 2 };
  const bw = Math.min(260, viewW - 64);
  const bh = 52, gap = 14;
  const px = (viewW - bw) / 2;
  const total = bh * 3 + gap * 2;
  const py = (viewH - total) / 2;
  const resume:  Rect = { x: px, y: py,                  w: bw, h: bh };
  const restart: Rect = { x: px, y: py + (bh + gap),     w: bw, h: bh };
  const hub:     Rect = { x: px, y: py + (bh + gap) * 2, w: bw, h: bh };
  const panel:   Rect = { x: px - 20, y: py - 24, w: bw + 40, h: total + 48 };
  return { toggle, panel, resume, restart, hub };
}

export function inRect(x: number, y: number, r: Rect): boolean {
  return x >= r.x && x <= r.x + r.w && y >= r.y && y <= r.y + r.h;
}

// Shown only during active play (not on the game-over screen, not before start).
export function menuToggleVisible(phase: GameState['phase'], pendingStart: boolean): boolean {
  return phase !== 'game_over' && !pendingStart;
}

export function drawMenu(
  ctx: CanvasRenderingContext2D,
  W: number, H: number,
  theme: Theme,
  phase: GameState['phase'],
  pendingStart: boolean,
  menuOpen: boolean,
): void {
  if (!menuToggleVisible(phase, pendingStart)) return;
  const { toggle, panel, resume, restart, hub } = menuRects(W, H);

  // ☰-кнопка
  ctx.save();
  ctx.fillStyle = 'rgba(255,255,255,0.12)';
  ctx.beginPath();
  ctx.arc(toggle.cx, toggle.cy, toggle.r, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = theme.textUi;
  ctx.font = 'bold 20px -apple-system, sans-serif';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText('☰', toggle.cx, toggle.cy + 1);
  ctx.restore();

  if (!menuOpen) return;

  // затемнение + панель
  ctx.save();
  ctx.fillStyle = 'rgba(0,0,0,0.5)';
  ctx.fillRect(0, 0, W, H);
  roundRect(ctx, panel.x, panel.y, panel.w, panel.h, 18);
  ctx.fillStyle = theme.gridBg;
  ctx.fill();
  ctx.strokeStyle = theme.gridBorder;
  ctx.lineWidth = 2;
  ctx.stroke();

  const items: [Rect, string, boolean][] = [
    [resume,  t('resume'),       false],
    [restart, t('restart_game'), false],
    [hub,     t('to_hub'),       true],   // акцентный
  ];
  for (const [r, label, accent] of items) {
    roundRect(ctx, r.x, r.y, r.w, r.h, 12);
    ctx.fillStyle = accent ? '#ffffff' : theme.emptyCell;
    ctx.fill();
    ctx.fillStyle = accent ? theme.bg : theme.textUi;
    ctx.font = 'bold 17px -apple-system, sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(label, r.x + r.w / 2, r.y + r.h / 2 + 1);
  }
  ctx.restore();
  ctx.textBaseline = 'alphabetic';
}
